import { formatInTimeZone } from 'date-fns-tz';

/**
 * Detect the user's browser timezone
 * @returns {string} IANA timezone string (e.g., "America/New_York", "Europe/Paris")
 */
export const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Failed to detect timezone:', error);
    return 'UTC'; // Fallback to UTC if detection fails
  }
};

/**
 * Get the user's timezone from user profile, localStorage, or detect it
 * @param {string} userProfileTimezone - Optional timezone from user's profile settings (takes precedence)
 * @returns {string} IANA timezone string
 */
export const getUserTimezone = (userProfileTimezone = null) => {
  try {
    // Priority 1: Use user's profile timezone if provided
    if (userProfileTimezone && userProfileTimezone !== 'undefined' && userProfileTimezone !== 'null') {
      // Sync localStorage with profile timezone
      localStorage.setItem('userTimezone', userProfileTimezone);
      return userProfileTimezone;
    }

    // Priority 2: Check localStorage
    const stored = localStorage.getItem('userTimezone');
    if (stored && stored !== 'undefined' && stored !== 'null') {
      return stored;
    }

    // Priority 3: Detect from browser
    const detected = detectTimezone();
    localStorage.setItem('userTimezone', detected);
    return detected;
  } catch (error) {
    console.error('Failed to get user timezone:', error);
    return 'UTC'; // Fallback to UTC
  }
};

/**
 * Set the user's timezone in localStorage
 * @param {string} timezone - IANA timezone string
 */
export const setUserTimezone = (timezone) => {
  try {
    localStorage.setItem('userTimezone', timezone);
  } catch (error) {
    console.error('Failed to set user timezone:', error);
  }
};

/**
 * Convert a Date object to an ISO string with timezone offset
 * Format: "2024-12-02T14:00:00+01:00"
 * @param {Date} date - JavaScript Date object
 * @param {string} timezone - IANA timezone string
 * @returns {string} ISO 8601 string with timezone offset
 */
export const dateToTimezoneString = (date, timezone) => {
  if (!date) return null;
  if (!(date instanceof Date)) {
    console.error('dateToTimezoneString: Invalid date object', date);
    return null;
  }

  try {
    return formatInTimeZone(date, timezone, "yyyy-MM-dd'T'HH:mm:ssXXX");
  } catch (error) {
    console.error('Failed to format date with timezone:', error, { date, timezone });
    return date.toISOString(); // Fallback to UTC ISO string
  }
};

/**
 * Parse an ISO string from the API
 * The API returns ISO strings with timezone offsets (e.g., "2024-12-02T14:00:00+05:30")
 * JavaScript Date constructor handles this correctly
 * @param {string} isoString - ISO 8601 string with timezone offset
 * @returns {Date|null} JavaScript Date object (in local timezone)
 */
export const parseFromAPI = (isoString) => {
  if (!isoString) return null;

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      console.error('Failed to parse ISO string:', isoString);
      return null;
    }
    return date;
  } catch (error) {
    console.error('Failed to parse ISO string from API:', error, isoString);
    return null;
  }
};

/**
 * Normalize timezone aliases to canonical IANA names
 * Handles legacy timezone names that map to current names
 * @param {string} timezone - IANA timezone string (may be legacy name)
 * @returns {string} Normalized canonical timezone name
 */
export const normalizeTimezone = (timezone) => {
  if (!timezone) return timezone;

  // Map of legacy/alias timezone names to canonical names
  const aliases = {
    'Asia/Calcutta': 'Asia/Kolkata',
    // Add more aliases as needed
  };

  return aliases[timezone] || timezone;
};

/**
 * Compare two timezones for equality, handling aliases
 * @param {string} tz1 - First timezone
 * @param {string} tz2 - Second timezone
 * @returns {boolean} True if timezones are equivalent
 */
export const timezonesEqual = (tz1, tz2) => {
  if (!tz1 || !tz2) return false;
  return normalizeTimezone(tz1) === normalizeTimezone(tz2);
};

/**
 * Check if a timezone string is valid
 * @param {string} timezone - IANA timezone string to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get the timezone offset in hours for a given timezone
 * @param {string} timezone - IANA timezone string
 * @returns {number} Offset in hours (e.g., 5.5 for UTC+05:30)
 */
export const getTimezoneOffset = (timezone) => {
  try {
    const now = new Date();
    const offsetStr = formatInTimeZone(now, timezone, 'XXX'); // e.g., "+05:30" or "-08:00"

    const sign = offsetStr[0] === '+' ? 1 : -1;
    const [hours, minutes] = offsetStr.substring(1).split(':').map(Number);

    return sign * (hours + minutes / 60);
  } catch (error) {
    console.error('Failed to get timezone offset:', error, timezone);
    return 0;
  }
};
